import React, { useMemo } from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { GenericOption } from './types';
import SearchableSelectDropdown from 'components/Dropdown/MultiFilterPanel/SearchableSelectDropdown';

interface FilterMultiSelectStyledProps {
  label: string;
  placeholder?: string;
  options: GenericOption[];
  value: GenericOption[];
  onChange: (newValue: GenericOption[]) => void;
  width?: number;
  pluralLabel?: string;
  isSingleSelect?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  inputBorder?: string;
  initialOptionLimit?: number;
  onOpen?: () => void;
  loading?: boolean;
  sx?: SxProps<Theme>; 
  isNestedBg?:boolean;
}

const ALL_OPTION: GenericOption = {
  label: 'All',
  value: 'all',
};

const FilterMultiSelectStyled: React.FC<FilterMultiSelectStyledProps> = ({
  label,
  placeholder = '',
  options,
  value,
  onChange,
  width = 424,
  pluralLabel = '',
  isSingleSelect = false,
  disabled = false,
  error = false,
  helperText,
  inputBorder,
  initialOptionLimit,
  onOpen,
  loading = false,
  sx, 
  isNestedBg = false,
}) => {
  const effectiveInputBorder = inputBorder ?? (error ? 'transparent' : 'none');
  const dropdownOptions = useMemo(() => {
    if (isSingleSelect) return options;

    const hasAllOption = options.some((option) => option.value === 'all');
    return hasAllOption ? options : [ALL_OPTION, ...options];
  }, [options, isSingleSelect]);

  const dropdownOptionsMap = useMemo(
    () => new Map(dropdownOptions.map((option) => [option.value, option])),
    [dropdownOptions],
  );

  const dropdownValue = useMemo(() => {
    if (isSingleSelect) {
      return value?.[0]?.value ?? null;
    }

    return value?.map((item) => item.value) ?? [];
  }, [value, isSingleSelect]);

  const handleDropdownChange = (nextValue: string[] | string | null) => {
    if (isSingleSelect) {
      if (!nextValue) {
        const defaultOption = dropdownOptionsMap.get('rest_of_day');

        onChange(defaultOption ? [defaultOption] : []);
        return;
      }

      const selectedOption = dropdownOptionsMap.get(nextValue as string);

      onChange(selectedOption ? [selectedOption] : []);
      return;
    }

    const nextValues = Array.isArray(nextValue) ? nextValue : [];

    const nextValuesSet = new Set(nextValues);
    const selectedOptions = dropdownOptions.filter((option) =>
      nextValuesSet.has(option.value),
    );

    onChange(selectedOptions);
  };

  return (
    <Box sx={{ mb: 3, width, ...sx }}> 
      <Box
        sx={{
          '& > div': {
            width: '100%',
          },
          '& .MuiPaper-root': {
            border: error ? '1px solid #D32F2F !important' : undefined,
          },
        }}
      >
        <SearchableSelectDropdown
          label={label}
          placeholder={placeholder}
          options={dropdownOptions}
          multiple={!isSingleSelect}
          value={dropdownValue}
          onChange={handleDropdownChange}
          showSearch
          showAllOption={!isSingleSelect}
          inputBorder={effectiveInputBorder}
          chipDisplay
          disabled={disabled}
          initialOptionLimit={initialOptionLimit}
          onOpen={onOpen}
          loading={loading}
          isNestedBg={isNestedBg}
        />
      </Box>
      {helperText ? (
        <Typography sx={{ mt: 0.5, color: '#D32F2F', fontSize: '0.875rem', pl: 0.5 }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default FilterMultiSelectStyled;